import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { PencilSquareIcon, HomeIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import formatDuration from '@/features/videos-common/utils/formatDuration';
import {
  HOME_VIDEO_AUDIENCES,
  INTENT_TAG_NAMES,
  isEligible,
  videoTagNames
} from '@/features/videos-common/audiences';
import { getSlotPosition } from '@/features/staff-settings-videos/utils/homeSet';
import AudienceChip from './AudienceChip';
import VideoThumbnail from './VideoThumbnail';
import ConfirmDeleteButton from './ConfirmDeleteButton';
import TextButton from './TextButton';

const videoShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  youtubeVideoId: PropTypes.string,
  thumbnailUrl: PropTypes.string,
  durationSeconds: PropTypes.number,
  isPublished: PropTypes.bool,
  audiences: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string
  })),
  slots: PropTypes.arrayOf(PropTypes.shape({
    audience: PropTypes.string,
    position: PropTypes.number
  }))
});

function PublishedBadge({ isPublished }) {
  const { t } = useTranslation();
  return (
    <span className={classNames(
      'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold',
      isPublished ? 'bg-green-50 text-cs-gray-900' : 'bg-cs-gray-100 text-cs-gray-700'
    )}>
      {isPublished ? t('staff_videos.list.published') : t('staff_videos.list.unpublished')}
    </span>
  );
}

PublishedBadge.propTypes = {
  isPublished: PropTypes.bool
};

function AudienceChips({ video }) {
  const { t } = useTranslation();
  const names = videoTagNames(video).filter((name) => INTENT_TAG_NAMES.includes(name));

  if (names.length === 0) {
    return (
      <AudienceChip tone="untagged" title={t('staff_videos.list.untagged_help')}>
        {t('staff_videos.list.untagged')}
      </AudienceChip>
    );
  }

  return (
    <div className="flex flex-wrap gap-1">
      {INTENT_TAG_NAMES.filter((name) => names.includes(name)).map((name) => (
        <AudienceChip key={name} tone={name.toLowerCase()}>
          {t(`staff_videos.audiences.${name.toLowerCase()}`)}
        </AudienceChip>
      ))}
    </div>
  );
}

AudienceChips.propTypes = {
  video: videoShape.isRequired
};

function SlotChips({ video, none }) {
  const { t } = useTranslation();
  const chips = HOME_VIDEO_AUDIENCES
    .map((audience) => ({ audience, position: getSlotPosition(video, audience) }))
    .filter((slot) => slot.position !== null);

  if (chips.length === 0) return none;

  return (
    <div className="flex flex-wrap gap-1">
      {chips.map(({ audience, position }) => (
        <AudienceChip key={audience} tone={audience}>
          {t('staff_videos.list.slot_chip', {
            audience: t(`staff_videos.audiences.${audience}`),
            position
          })}
        </AudienceChip>
      ))}
    </div>
  );
}

SlotChips.propTypes = {
  video: videoShape.isRequired,
  none: PropTypes.node
};

/**
 * All videos (including unpublished) with row actions.
 * Pin/Unpin act on the home set of `homeAudience` (the selected tab) and are
 * rendered only when onPin/onUnpin are provided. Pin is disabled, with a
 * visible reason, when the video is not tagged for that audience or the
 * set is full.
 */
VideosTable.propTypes = {
  videos: PropTypes.arrayOf(videoShape).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  deletingId: PropTypes.number,
  onPin: PropTypes.func,
  onUnpin: PropTypes.func,
  homeAudience: PropTypes.oneOf(HOME_VIDEO_AUDIENCES).isRequired,
  canPin: PropTypes.bool,
  homeBusy: PropTypes.bool,
  pinDisabledReasonId: PropTypes.string
};

export default function VideosTable(props) {
  const {
    videos,
    onEdit,
    onDelete,
    deletingId = null,
    onPin,
    onUnpin,
    homeAudience,
    canPin = true,
    homeBusy = false,
    pinDisabledReasonId
  } = props;
  const { t } = useTranslation();
  const none = <span className="text-cs-gray-500">{t('staff_videos.list.none')}</span>;
  const audienceLabel = t(`staff_videos.audiences.${homeAudience}`);

  return (
    <div className="overflow-x-auto bg-white border border-cs-gray-200 rounded-cs-10">
      <table className="min-w-full divide-y divide-cs-gray-200 text-left">
        <thead className="bg-cs-gray-50">
          <tr>
            {['video', 'duration', 'status', 'audience', 'home', 'actions'].map((column) => (
              <th
                key={column}
                scope="col"
                className="px-4 py-3 text-xs font-semibold text-cs-gray-700 whitespace-nowrap"
              >
                {t(`staff_videos.list.columns.${column}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-cs-gray-100">
          {videos.map((video) => {
            const duration = formatDuration(video.durationSeconds);
            const isPinned = getSlotPosition(video, homeAudience) !== null;
            const eligible = isEligible(video, homeAudience);
            const isDeleting = deletingId === video.id;
            const ineligibleId = `staff-video-${video.id}-pin-reason`;
            let pinReasonId;
            if (!eligible) pinReasonId = ineligibleId;
            else if (!canPin) pinReasonId = pinDisabledReasonId;

            return (
              <tr key={video.id} data-testid="staff-video-row">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3 min-w-[16rem]">
                    <VideoThumbnail url={video.thumbnailUrl} alt="" />
                    <span className="text-sm font-semibold text-cs-gray-900">{video.title}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-cs-gray-900 tabular-nums">
                  {duration || none}
                </td>
                <td className="px-4 py-3">
                  <PublishedBadge isPublished={video.isPublished} />
                </td>
                <td className="px-4 py-3">
                  <AudienceChips video={video} />
                </td>
                <td className="px-4 py-3 text-sm">
                  <SlotChips video={video} none={none} />
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 min-w-[14rem]">
                    <TextButton
                      label={t('staff_videos.list.edit')}
                      icon={PencilSquareIcon}
                      aria-label={t('staff_videos.list.edit_label', { title: video.title })}
                      onClick={() => onEdit(video)}
                      disabled={isDeleting}
                    />
                    {isPinned && onUnpin && (
                      <TextButton
                        label={t('staff_videos.list.unpin')}
                        icon={HomeIcon}
                        aria-label={t('staff_videos.list.unpin_label', { title: video.title, audience: audienceLabel })}
                        onClick={() => onUnpin(video)}
                        disabled={homeBusy || isDeleting}
                      />
                    )}
                    {!isPinned && onPin && (
                      <TextButton
                        label={t('staff_videos.list.pin')}
                        icon={HomeIcon}
                        aria-label={t('staff_videos.list.pin_label', { title: video.title, audience: audienceLabel })}
                        aria-describedby={pinReasonId}
                        onClick={() => onPin(video)}
                        disabled={!eligible || !canPin || homeBusy || isDeleting}
                      />
                    )}
                    <ConfirmDeleteButton
                      title={video.title}
                      deleting={isDeleting}
                      disabled={deletingId !== null && !isDeleting}
                      onConfirm={() => onDelete(video)}
                    />
                  </div>
                  {!isPinned && onPin && !eligible && (
                    <p id={ineligibleId} className="mt-1 text-xs text-cs-gray-600 max-w-[14rem]">
                      {t(`staff_videos.list.pin_ineligible_${homeAudience}`)}
                    </p>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
